/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    IAny,
    ICreateActionContext,
    IGeneric
} from '@msdyn365-commerce/core';
import { BypassCache, FeatureState, ProductDimension, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { getVariantsByDimensionValuesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';

import { FeatureStateInput, getFeatureStateAction } from './get-feature-state';
import { ArrayExtensions } from './utilities';
import { isCrossCompanyInventoryLookupEcommerceEnabled } from './utilities/cross-entity-inventory-feature';
import { QueryResultSettingsProxy } from './utilities/QueryResultSettingsProxy';
import { generateProductImageUrl, getDimensionValuesFromQuery, getSelectedProductIdFromActionInput } from './utilities/utils';

/**
 * The source that produced the resolved variant.
 * - `CrossLE`: resolved via the Cross Legal Entity gated path (GetVariantsByDimensionValues API).
 * - `OldIV`: caller should keep using its existing selected-variant flow (old inventory APIs).
 */
export type CrossEntityVariantResolutionSource = 'CrossLE' | 'OldIV';

/**
 * Result of Cross-LE variant resolution.
 * When `source` is `OldIV`, `variant`/`variantId` are undefined and the caller
 * must fall back to the existing `get-selected-variant` flow.
 */
export interface ICrossEntityVariantResolution {
    variant?: SimpleProduct;
    variantId?: number;
    source: CrossEntityVariantResolutionSource;
}

/**
 * Resolves a concrete variant id for Cross-LE PDP availability.
 *
 * Phase 1 keeps the existing `get-selected-variant` flow as the safe default.
 * When the Cross-LE feature is enabled and dimensions are selected, this attempts the
 * Cross-LE `getVariantsByDimensionValues` path and takes the first variant. Any failure,
 * empty result, or disabled feature returns `{ source: 'OldIV' }` so callers keep their
 * existing (old inventory) resolution — no behavior change when the feature is off.
 * @param context - The action context.
 * @param productId - The master/record product id.
 * @param channelId - The channel id.
 * @param matchingDimensionValues - The selected dimension values (partial or complete).
 * @param options - Optional feature state and cache-bypass flag.
 * @returns The variant resolution, or a `Legacy` signal.
 */
export async function resolveCrossEntitySelectedVariant(
    context: IActionContext,
    productId: number,
    channelId: number,
    matchingDimensionValues?: ProductDimension[],
    options?: { featureState?: FeatureState[]; bypassCache?: BypassCache }
): Promise<ICrossEntityVariantResolution> {
    const gated = isCrossCompanyInventoryLookupEcommerceEnabled(options?.featureState);

    // OldIV is the safe default: feature off, no product, or no dimensions selected yet.
    if (!gated || !productId || !ArrayExtensions.hasElements(matchingDimensionValues)) {
        return { source: 'OldIV' };
    }

    try {
        const variants = await getVariantsByDimensionValuesAsync(
            {
                callerContext: context,
                queryResultSettings: QueryResultSettingsProxy.getPagingFromInputDataOrDefaultValue(context),
                bypassCache: options?.bypassCache
            },
            productId,
            channelId,
            matchingDimensionValues
        );

        if (ArrayExtensions.hasElements(variants) && variants[0]?.RecordId) {
            const variant: SimpleProduct = { ...variants[0] };
            const imageUrl = generateProductImageUrl(variant, context.requestContext.apiSettings);
            if (imageUrl) {
                variant.PrimaryImageUrl = imageUrl;
            }
            context.telemetry.debug('[ResolveCrossEntitySelectedVariant] resolved via GetVariantsByDimensionValues');
            return { variant, variantId: variant.RecordId, source: 'CrossLE' };
        }

        context.telemetry.debug(
            '[ResolveCrossEntitySelectedVariant] GetVariantsByDimensionValues returned no variants; falling back to old inventory'
        );
    } catch (error) {
        context.telemetry.error((error as Error).message);
        context.telemetry.debug('[ResolveCrossEntitySelectedVariant] error resolving variant; falling back to old inventory');
    }

    return { source: 'OldIV' };
}

/**
 * Input for the getCrossEntitySelectedVariant data action.
 */
export class CrossEntitySelectedVariantInput implements IActionInput {
    public productId: number;

    public channelId: number;

    public matchingDimensionValues?: ProductDimension[];

    public featureState?: FeatureState[];

    public bypassCache?: BypassCache;

    public constructor(
        productId: number,
        channelId: number,
        matchingDimensionValues?: ProductDimension[],
        featureState?: FeatureState[],
        bypassCache?: BypassCache
    ) {
        this.productId = productId;
        this.channelId = channelId;
        this.matchingDimensionValues = matchingDimensionValues;
        this.featureState = featureState;
        this.bypassCache = bypassCache;
    }

    public getCacheKey = (): string => 'CrossEntitySelectedVariant';

    public getCacheObjectType = (): string => 'CrossEntitySelectedVariant';

    public dataCacheType = (): CacheType => 'none';
}

/**
 * CreateInput method for the getCrossEntitySelectedVariant data action.
 * @param inputData - The create-action context.
 * @returns The input instance.
 */
export const createCrossEntitySelectedVariantInput = (inputData: ICreateActionContext<IGeneric<IAny>>): CrossEntitySelectedVariantInput => {
    const productId = getSelectedProductIdFromActionInput(inputData);
    if (productId) {
        return new CrossEntitySelectedVariantInput(+productId, +inputData.requestContext.apiSettings.channelId);
    }
    throw new Error('Unable to create CrossEntitySelectedVariantInput, no productId found on module config or query');
};

/**
 * Action method for the getCrossEntitySelectedVariant data action.
 * Fetches feature state (when not provided) and delegates to the resolver.
 * @param input - The action input.
 * @param context - The action context.
 * @returns The variant resolution.
 */
export async function getCrossEntitySelectedVariantAction(
    input: CrossEntitySelectedVariantInput,
    context: IActionContext
): Promise<ICrossEntityVariantResolution> {
    let featureState = input.featureState;
    if (!featureState) {
        try {
            featureState = await getFeatureStateAction(new FeatureStateInput(), context);
        } catch (error) {
            context.telemetry.error('[GetCrossEntitySelectedVariantAction] Unable to fetch feature state', error as Error);
            featureState = undefined;
        }
    }

    const matchingDimensionValues = input.matchingDimensionValues ?? getDimensionValuesFromQuery(context.requestContext.url.requestUrl);

    return resolveCrossEntitySelectedVariant(context, input.productId, input.channelId, matchingDimensionValues, {
        featureState,
        bypassCache: input.bypassCache
    });
}

/**
 * The complete getCrossEntitySelectedVariant data action.
 */
export const getCrossEntitySelectedVariantDataAction = createObservableDataAction({
    id: '@msdyn365-commerce-modules/retail-actions/get-cross-entity-selected-variant',
    action: getCrossEntitySelectedVariantAction as IAction<ICrossEntityVariantResolution>,
    input: createCrossEntitySelectedVariantInput
});

export default getCrossEntitySelectedVariantDataAction;
